import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ADMIN_EMAIL, checkAdminAccess } from "@/api/admin";
import { ADMIN_LOGIN_PATH } from "@/components/admin/adminConstants";
import { useNoIndex } from "@/lib/noIndex";
import { useAuth } from "@/contexts/AuthContext";

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { loading, session, user, configured } = useAuth();
  const location = useLocation();

  useNoIndex();
  const shouldCheckAdmin = Boolean(
    session &&
      user &&
      user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase(),
  );
  const [checking, setChecking] = useState(shouldCheckAdmin);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!shouldCheckAdmin) {
      return;
    }

    let cancelled = false;
    setChecking(true);
    checkAdminAccess()
      .then((ok) => {
        if (!cancelled) setIsAdmin(ok);
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldCheckAdmin]);

  if (!configured) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6 text-center">
        <p className="text-sm text-ink-muted">Configure Supabase in web/.env</p>
      </div>
    );
  }

  if (loading || checking) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!session) {
    return <Navigate to={ADMIN_LOGIN_PATH} replace state={{ from: location.pathname }} />;
  }

  if (!shouldCheckAdmin || !isAdmin) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-bold">Access denied</h1>
        <p className="max-w-md text-sm text-ink-muted">
          This area is restricted to the Daywia admin account ({ADMIN_EMAIL}).
        </p>
        <a href="/" className="text-sm font-semibold text-primary hover:underline">
          Back to Daywia
        </a>
      </div>
    );
  }

  return children;
}
